// @flow
import * as React from 'react';
import L from 'leaflet';
import { get } from 'lodash';
import { LAT_LNG_MAX_MARGIN } from 'esm-web-pro/containers/pages/network/Util';

const REVERSE_CLASS_NAME = 'reverse-animation';

const layerIsFromPath = (layer, path, index) => {
    const startLatLng = L.latLng(path[index][0], path[index][1]);
    const endLatLng = L.latLng(path[index + 1][0], path[index + 1][1]);

    const [layerFirstCoordinate, layerSecondCoordinate] = layer.getLatLngs();
    if (layerFirstCoordinate.equals(startLatLng, LAT_LNG_MAX_MARGIN)
        && layerSecondCoordinate.equals(endLatLng, LAT_LNG_MAX_MARGIN)) {
        return [layer.getLatLngs().every(
            (layerCoordinate, layerIndex) => layerCoordinate.equals(path[index + layerIndex], LAT_LNG_MAX_MARGIN),
        ), false];
    }

    const indexDiff = layer.getLatLngs().length - 2;

    if (layerFirstCoordinate.equals(endLatLng, LAT_LNG_MAX_MARGIN)
        && layerSecondCoordinate.equals(startLatLng, LAT_LNG_MAX_MARGIN)) {
        return [layer.getLatLngs().slice().reverse().every(
            (layerCoordinate, layerIndex) => layerCoordinate
                .equals(path[index - indexDiff + layerIndex], LAT_LNG_MAX_MARGIN),
        ), true];
    }
    return [false, null];
};

/**
 * Function that finds layer for specific coordinates and applies a style on each layer.
 * At the end a callback is called with all layers that correspond to the coordinates.
 *
 * @param {[]} path - Array with coordinates.
 * @param {[ style, reverseStyle]} styles to apply
 * @param {[]} additionalStyles - Array with css attributes for second style of the line. Actually a new line is
 * created.
 * @param {{}} segmentColors - allows to specify colors for the individual segments of the line.
 * The argument should be an object where keys are the feature IDs of the lines and the colors are values.
 * @returns {*[]} Callback that needs to be used for every layer
 */
const UseHighlightLine = (path: Array<[number, number]>,
    [style: Object, reverseStyle: Object]: Object[],
    additionalStyles: ?Array<Object> = null, segmentColors: ?Object) => {
    let layers = [];
    const addedLayers = [];

    return {
        start: () => {
            if (additionalStyles) {
                additionalStyles.forEach((additionalStyle) => {
                    if (additionalStyle.featureGroup) {
                        additionalStyle.featureGroup.clearLayers();
                    }
                });
            }

            layers = [];
        },
        run: (layer: Object, featureGroup: Object = null) => {
            if (!(get(layer, 'feature.geometry.type') === 'LineString')) return;
            // The path is an array with lat lng coordinates
            path.forEach((coordinate, index) => {
                if ((index === path.length - 1)) return;

                const [fromPath, reverse] = layerIsFromPath(layer, path, index);
                if (fromPath) {
                    let layerColor;
                    if (segmentColors && Object.prototype.hasOwnProperty.call(segmentColors, layer.feature.id)) {
                        layerColor = segmentColors[layer.feature.id];
                    } else if (layer.initialOptions) {
                        layerColor = layer.initialOptions.color;
                    } else {
                        layerColor = layer.feature.properties.color || layer.options.color;
                    }

                    if (additionalStyles) {
                        additionalStyles.forEach((additionalStyle) => {
                            const newLine = L.polyline(layer.getLatLngs()).setStyle(
                                { color: layerColor, ...additionalStyle.style },
                            );

                            if (additionalStyle.featureGroup) {
                                addedLayers.push({ featureGroup: additionalStyle.featureGroup, newLine });
                            } else if (featureGroup) {
                                addedLayers.push({ featureGroup, newLine });
                            }
                        });
                    }
                    layer.setStyle({ color: layerColor, ...style });
                    if (layer._path && layer.options.className) {
                        if (reverse) {
                            L.DomUtil.addClass(layer._path, REVERSE_CLASS_NAME);
                        }
                        L.DomUtil.addClass(layer._path, layer.options.className);
                    }

                    layers.push({ featureGroup, layer });
                }
            });
        },
        end: () => {
            addedLayers.forEach(({ featureGroup, newLine }) => {
                featureGroup.addLayer(newLine);
                newLine.bringToBack();
            });

            layers.forEach(({ featureGroup, layer }) => {
                layer.bringToFront();
            });

            if (additionalStyles) {
                additionalStyles.forEach((additionalStyle) => {
                    if (additionalStyle.featureGroup) {
                        additionalStyle.featureGroup.bringToBack();
                    }
                });
            }
        },

        cleanUp: () => {
            layers.forEach(({ featureGroup, layer }) => {
                if (layer._path && layer.options.className) {
                    L.DomUtil.removeClass(layer._path, layer.options.className);
                    L.DomUtil.removeClass(layer._path, REVERSE_CLASS_NAME);
                }
                layer.setStyle({ ...reverseStyle, color: layer.feature.properties.color });
            });
            addedLayers.forEach(({ featureGroup, newLine }) => {
                featureGroup.removeLayer(newLine);
                newLine.remove();
            });
        },
    };
};

export { layerIsFromPath };

export default UseHighlightLine;
